import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import LoadingSpinner from '@components/common/LoadingSpinner'
import { HOME_ROUTE } from '@config/featureFlags'
import { isSharedHost, clearStoredSite, clearSiteHandoff } from '@config/tenant'

export default function SSOCallback() {
  const { isLoading, isAuthenticated, error } = useAuth0()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [callbackError, setCallbackError] = useState(null)

  const hasCode = searchParams.has('code')
  const hasError = searchParams.has('error')
  const errorDescription = searchParams.get('error_description')

  useEffect(() => {
    if (hasError) {
      setCallbackError(errorDescription || searchParams.get('error'))
      return
    }

    if (!hasCode) {
      navigate('/login', { replace: true })
      return
    }

    if (!isLoading) {
      if (isAuthenticated) {
        // The Auth0 round trip completed — retire the handoff marker (CC-53416)
        // so the NEXT visit to /login takes the fast path instead of being read
        // as a failed attempt.
        clearSiteHandoff()
        navigate(HOME_ROUTE, { replace: true })
      } else if (error) {
        setCallbackError(error.message)
      } else {
        setCallbackError('Authentication failed. Please try again.')
      }
    }
  }, [isLoading, isAuthenticated, error, hasCode, hasError, navigate, searchParams, errorDescription])

  // On the shared host, forget the failed site so returning to /login shows the
  // site-name prompt again instead of auto-retrying the same bad site (which would
  // loop the user through this error with no escape — no header/"Switch site" here).
  // The handoff marker goes too: with no stored site the prompt shows regardless,
  // and leaving a stale marker behind would make the user's NEXT attempt at that
  // same site look like a failure before it had a chance to run (CC-53416).
  useEffect(() => {
    if (callbackError && isSharedHost()) {
      clearStoredSite()
      clearSiteHandoff()
    }
  }, [callbackError])

  if (callbackError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Error</h2>
          <p className="text-gray-600 mb-6">{callbackError}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="py-3 px-6 bg-cb-accent-text text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            {isSharedHost() ? 'Choose a different site' : 'Return to Login'}
          </button>
        </div>
      </div>
    )
  }

  return <LoadingSpinner fullScreen message="Completing login..." />
}
